// Plan 75 — password-reset landing page.
//
// Reached via the link sent by `POST /auth/password-reset/request`. The
// raw token rides in `?token=` on the URL; the page does NOT render the
// app shell (no Navbar, no Footer, no AuthModal), because this is a
// single-purpose external destination — the user followed an email link
// from a logged-out state and we don't want any cross-route distraction.
//
// On success the page swaps to a confirmation panel and offers a button
// to head back to the home page (where the user can sign in normally).

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, ErrorAlert, FormInput } from '../../shared/ui';
import { extractErrorCode, messageFor } from '../../shared/errors/messageFor';
import { confirmPasswordReset } from './authApi';

interface ResetFormFields {
  new_password: string;
  confirm_password: string;
}

function tokenFromUrl(): string {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('token') ?? '';
}

export const ResetPasswordPage = () => {
  const token = tokenFromUrl();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const { register, handleSubmit, getValues } = useForm<ResetFormFields>({
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const onSubmit = async (fields: ResetFormFields) => {
    setError('');
    if (fields.new_password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (fields.new_password !== fields.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, fields.new_password);
      setDone(true);
    } catch (err) {
      setError(
        messageFor(
          extractErrorCode(err),
          'Reset link is invalid or has expired. Please request a new one.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Strip the token from the URL when sending the user home so the
  // single-use token doesn't linger in browser history.
  const goHome = () => {
    window.location.assign('/');
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6">
        <div className="bg-white p-12 rounded-[3rem] border border-stone-200 max-w-md w-full space-y-6 text-center">
          <h1 className="text-3xl font-serif">Reset Link Invalid</h1>
          <p className="text-stone-500 text-sm leading-relaxed">
            This page expects a token. Please request a new password-reset link from the sign-in
            screen.
          </p>
          <Button type="button" fullWidth size="lg" onClick={goHome}>
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-12">
      <div className="bg-white p-12 rounded-[3rem] border border-stone-200 max-w-md w-full">
        <h1 className="text-3xl font-serif mb-2">Reset Your Password</h1>
        {done ? (
          <div className="space-y-6 mt-4">
            <p className="text-sm text-stone-600 leading-relaxed">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Button type="button" fullWidth size="lg" onClick={goHome}>
              Continue to Sign In
            </Button>
          </div>
        ) : (
          <>
            <p className="text-stone-400 text-xs uppercase tracking-widest font-bold mb-8">
              Choose a new password
            </p>
            {error && <ErrorAlert message={error} className="mb-4" />}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <FormInput
                label="New Password"
                type="password"
                density="compact"
                placeholder="At least 8 characters"
                autoComplete="new-password"
                {...register('new_password', { required: true, minLength: 8 })}
              />
              <FormInput
                label="Confirm New Password"
                type="password"
                density="compact"
                placeholder="Re-enter your new password"
                autoComplete="new-password"
                {...register('confirm_password', {
                  required: true,
                  validate: (v) => v === getValues('new_password') || 'Passwords do not match.',
                })}
              />
              <Button type="submit" fullWidth size="lg" loading={submitting} disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Password'}
              </Button>
              <p className="text-center text-xs text-stone-400 mt-4">
                Reset links expire 30 minutes after issuance and can only be used once.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
